import { useState, useEffect, useMemo } from "react";
import { Product, Stock } from "../Types";
import { useVariantsQuery } from "../Services";

export const getStockVariantTag = (stock: Stock): string => {
  let tag = stock.variant_name || "";
  if (!tag && stock.variant_data) {
    try {
      const parsed =
        typeof stock.variant_data === "string"
          ? JSON.parse(stock.variant_data)
          : stock.variant_data;
      if (Array.isArray(parsed)) {
        tag = parsed
          .map((d: any) => d.option_name || d.name)
          .filter(Boolean)
          .join(" / ");
      }
    } catch {}
  }
  return tag;
};

interface UseVariantFilterParams {
  product: Product;
  selectedBatch: Stock | null;
  setSelectedBatch: (batch: Stock | null) => void;
  setCustomPrice: (price: number) => void;
  isWholeSale: boolean;
}

export const useVariantFilter = ({
  product,
  selectedBatch,
  setSelectedBatch,
  setCustomPrice,
  isWholeSale,
}: UseVariantFilterParams) => {
  const { data: masterVariations = [] } = useVariantsQuery();
  const [selectedVariantFilters, setSelectedVariantFilters] = useState<
    Record<string, string>
  >({});

  const hasVariants = useMemo(() => {
    return Boolean(
      product.stocks?.some(
        (s) =>
          s.variant_name ||
          (Array.isArray(s.variant_data) && s.variant_data.length > 0) ||
          (typeof s.variant_data === "string" && s.variant_data.length > 2)
      )
    );
  }, [product.stocks]);

  useEffect(() => {
    setSelectedVariantFilters({});
  }, [product.id]);

  const activeVariantSchemaIds = useMemo(() => {
    const ids = new Set<string>();
    product.stocks?.forEach((st) => {
      let vData: any[] = [];
      if (Array.isArray(st.variant_data)) {
        vData = st.variant_data;
      } else if (typeof st.variant_data === "string") {
        try {
          vData = JSON.parse(st.variant_data);
        } catch {}
      }
      vData.forEach((item: any) => {
        if (item.variant_id) ids.add(item.variant_id);
        if (item.variation_id) ids.add(item.variation_id);
      });
    });
    return ids;
  }, [product.stocks]);

  const relevantMasterVariations = useMemo(() => {
    if (activeVariantSchemaIds.size > 0) {
      return masterVariations.filter((mv) => activeVariantSchemaIds.has(mv.id));
    }
    return masterVariations;
  }, [masterVariations, activeVariantSchemaIds]);

  const filteredStocks = useMemo(() => {
    if (!product.stocks) return [];
    const activeEntries = Object.entries(selectedVariantFilters).filter(
      ([_, optId]) => Boolean(optId)
    );
    if (activeEntries.length === 0) return product.stocks;

    return product.stocks.filter((st) => {
      let vData: any[] = [];
      if (Array.isArray(st.variant_data)) {
        vData = st.variant_data;
      } else if (typeof st.variant_data === "string") {
        try {
          vData = JSON.parse(st.variant_data);
        } catch {}
      }
      return activeEntries.every(([vSchemaId, selectedOptId]) => {
        return vData.some(
          (item: any) =>
            (item.variant_id === vSchemaId || item.variation_id === vSchemaId) &&
            item.option_id === selectedOptId
        );
      });
    });
  }, [product.stocks, selectedVariantFilters]);

  const handleVariantFilterChange = (
    vSchemaId: string,
    optId: string | null
  ) => {
    const updatedFilters = {
      ...selectedVariantFilters,
      [vSchemaId]: optId || "",
    };
    if (!optId) {
      delete updatedFilters[vSchemaId];
    }
    setSelectedVariantFilters(updatedFilters);

    const activeEntries = Object.entries(updatedFilters).filter(
      ([_, idVal]) => Boolean(idVal)
    );
    const matchingStocks = (product.stocks || []).filter((st) => {
      let vData: any[] = [];
      if (Array.isArray(st.variant_data)) {
        vData = st.variant_data;
      } else if (typeof st.variant_data === "string") {
        try {
          vData = JSON.parse(st.variant_data);
        } catch {}
      }
      return activeEntries.every(([sId, selectedOptId]) => {
        return vData.some(
          (item: any) =>
            (item.variant_id === sId || item.variation_id === sId) &&
            item.option_id === selectedOptId
        );
      });
    });

    if (matchingStocks.length > 0) {
      const bestMatch =
        matchingStocks.find((s) => s.id === selectedBatch?.id) ||
        matchingStocks[0];
      if (bestMatch && bestMatch.id !== selectedBatch?.id) {
        setSelectedBatch(bestMatch);
        setCustomPrice(
          isWholeSale ? bestMatch.wholesale_price : bestMatch.retail_price
        );
      }
    }
  };

  return {
    hasVariants,
    relevantMasterVariations,
    selectedVariantFilters,
    filteredStocks,
    handleVariantFilterChange,
    getStockVariantTag,
  };
};
